import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import SearchBox from "components/__new/common/SearchBox";
import Table from "components/__new/common/Table";
import DownloadAsCSV from "components/__new/common/DownloadAsCSV";
import DropdownNew from "utils/dropdown";
import Print from "components/__new/common/Print";
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import Filter, { ActiveFilter } from "components/__new/common/Filter";
import useUrlQueryParams from "hooks/useUrlQueryParams";
import { TableContent } from "components/__new/common/TableContent";
import DateFormat, { MonthName } from "utils/dateFormat";
import CurrencyFormat from "utils/currencyFormat";
import Pagination from "components/__new/common/pagination";
import Maybe from "components/__new/common/Maybe";
import imgBarchart from "assets/images/icons/barchart.svg";
import processParams from "utils/processParams";
import TabSettings from "components/__new/common/TabSettings";
import DashboardBox from "components/__new/common/DashboardBox";
import NumberFormat from "utils/numberFormat";
import { renderStatusColor } from "utils/statusColor";
import { toast } from "react-toastify";
import { ReactComponent as IconEdit } from "assets/images/icons/project-icons/edit-pen.svg";
import { ReactComponent as IconDownload } from "assets/images/icons/project-icons/download-3.svg";
import { ReactComponent as IconStack } from "assets/images/icons/project-icons/menu-stack.svg";
import * as paymentNoteAction from "store/entities/invoice/action";


export const List = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const {urlQueryParams} = useUrlQueryParams();
    const { paymentNotesLoading, paymentNotes, paymentNotesDownloadLoading, paymentNotesDownload, invoicePDFLoading, invoicePDF } = useSelector((s) => s.entities.invoice);
    
    
    const downloadAsCSVRef = useRef();
    const printComponentRef = useRef();
    const [downloadInvoicePDFInitiated, setDownloadInvoicePDFInitiated] = useState(false);
    const [month, setMonth] = useState("");
    const [year, setYear] = useState("");
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 20,
    });


    const getPaymentNotes = () => {
        let params = {...listParams};

        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 20, "limit");
        params = processParams(urlQueryParams, params, "search", "string", "", "keyword");
        params = processParams(urlQueryParams, params, "status", "string", "", "status");
        
        setListParams(params);        
        dispatch(paymentNoteAction.getAllInvoice(params));
    }


    const downloadInvoicePDF = (paymentNote) => {
        const downloadInvoicePDFParams = {
            paymentId: paymentNote.invoiceId,
        }
        setMonth(MonthName(paymentNote.month));
        setYear(paymentNote.year);
        setDownloadInvoicePDFInitiated(true);
        dispatch(paymentNoteAction.downloadInvoicePDF(downloadInvoicePDFParams));
        toast("Downloading PDF");
    }


    const showCurrentMonthYear = () => {
        const date = new Date();
        const currentMonthIndex = (date.getMonth() + 1);
        let month = MonthName(currentMonthIndex);
        let year = date.getFullYear();
        return month + ", " + year;
    }


    useEffect(() => {
        getPaymentNotes();
        // eslint-disable-next-line
    }, [location]);


    useEffect(() => {
        if (downloadInvoicePDFInitiated === true){
            if (invoicePDFLoading === false){
                if (invoicePDF?.status === "success"){
                    const linkSource = `data:application/pdf;base64,${invoicePDF?.pdf}`;
                    const downloadLink = document.createElement("a");
                    const fileName = `Payment Note (${month} ${year}).pdf`;
                    downloadLink.href = linkSource;
                    downloadLink.download = fileName;
                    downloadLink.click();
                    toast.success("PDF downloaded");
                }
                else{
                    toast.error("An error occurred while downloading payment note.");
                }

                setDownloadInvoicePDFInitiated(false);
            }
        }
        // eslint-disable-next-line
    }, [invoicePDFLoading, invoicePDF]);

    
    return (
        <div>

            <DynamicHeadTag
                headerText="Payment note"
                headerDescription="See all pending payment transactions for easy tracking and record-keeping purposes."
            />

            <div className="border-b">
                <TabSettings options={[
                    {
                        title: <span>Payment Notes</span>,
                        type: "link",
                        url: "/payment-note",
                    },
                    {
                        title: <span>Payment History</span>,
                        type: "link",
                        url: "/payment-history",
                    },
                ]} />
            </div>


            <div className="mt-6 lg:flex">
                <div className="w-full lg:w-1/2">
                    <div className="page-title">
                        Payment Notes
                    </div>
                </div>
                <div className="w-full lg:w-1/2 flex mt-4 lg:mt-0 lg:justify-end space-x-3"></div>
            </div>


            <div className="mt-6">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    <DashboardBox
                        title="Total Withdrawals"
                        loading={paymentNotesLoading}
                        value={NumberFormat(paymentNotes.totalWithdrawals, "no-kobo") || "-"}
                        subtext={<div className="text-fade text-xs -mt-2">As at {showCurrentMonthYear()}</div>}
                        icon={<><img src={imgBarchart} alt="dashboard" className="w-9 h-9 object-contain" /></>}
                    />
                    <DashboardBox
                        title="Payment Due"
                        loading={paymentNotesLoading}
                        value={CurrencyFormat(paymentNotes.amountDue) || "-"}
                        subtext={<div className="text-fade text-xs -mt-2">(Including transaction fees)</div>}
                        icon={<><img src={imgBarchart} alt="dashboard" className="w-9 h-9 object-contain" /></>}
                    />
                    <DashboardBox
                        title="Due Date"
                        loading={paymentNotesLoading}
                        value={DateFormat(paymentNotes.dueDate) || "-"}
                        icon={<><img src={imgBarchart} alt="dashboard" className="w-9 h-9 object-contain" /></>}
                    />
                </div>
            </div>

        
            <div className="mt-6 box !px-0 !pb-0">
                <div className="px-4 flex flex-wrap justify-between">
                    <div className="max-w-sm flex-grow sm:mr-6">
                        <SearchBox placeholder="Search by payment id, amount..." name="search" extraUrlParams="page=1" />
                    </div>
                    <div className="flex flex-wrap space-x-3 sm:space-x-6">
                        <div>
                            <Filter filters={[
                                {
                                    title: "Status",
                                    name: "status",
                                    dataType: "select",
                                    options: [
                                        {
                                            display: "All",
                                            payload: "",
                                        },
                                        {
                                            display: "Paid",
                                            payload: "paid",
                                        },
                                        {
                                            display: "Unpaid",
                                            payload: "unpaid",
                                        },
                                    ],
                                },
                            ]}/>
                        </div>
                        
                        <div>
                            <DownloadAsCSV
                                ref={downloadAsCSVRef}
                                tooltip="Download payment notes"
                                filename="Payment Notes.csv"
                                columns={[
                                    { label: "Date", key: "createdAt" },
                                    { label: "Payment ID", key: "invoiceId" },
                                    { label: "Month", key: "month" },
                                    { label: "Year", key: "year" },
                                    { label: "Amount", key: "amount" },
                                    { label: "Amount", key: "amountPaid" },
                                    { label: "Status", key: "status" },
                                ]}
                                click={() => {
                                    dispatch(paymentNoteAction.downloadPaymentNotes({...listParams, page: 1, limit: 10000})).then(() => {
                                        downloadAsCSVRef.current?.downloadData();
                                    });
                                }}
                                loading={paymentNotesDownloadLoading}
                                response={paymentNotesDownload?.invoices?.docs}
                            />
                        </div>

                        <div>
                            <Print component={printComponentRef} />
                        </div>
                    </div>
                </div>


                <div>
                    <ActiveFilter filters={{"Status": "status"}} />
                </div>

                
                <div className="mt-6">
                    <div ref={printComponentRef}>
                        
                        <div className="mb-4 text-xl hidden print:block">
                            Payment Notes
                        </div>
                        
                        <Table>
                            <Table.Head>
                                <th>Payment ID</th>
                                <th>Month</th>
                                <th>Amount</th>
                                <th>Amount Paid</th>
                                <th>Status</th>
                                <th>Pay</th>
                                <th></th>
                            </Table.Head>
                            <Table.Body>

                                <TableContent
                                    loading={paymentNotesLoading}
                                    loadingText="Loading payment notes"
                                    data={paymentNotes?.invoices}
                                    colspan={7}
                                />

                                {paymentNotesLoading === false && paymentNotes?.invoices?.docs?.map((paymentNote) =>
                                    <tr key={paymentNote._id}>
                                        <td>
                                            {paymentNote.invoiceId}
                                        </td>
                                        <td>
                                            {MonthName(paymentNote.month)}, {paymentNote.year}
                                        </td>
                                        <td>
                                            {CurrencyFormat(paymentNote.amount)}
                                        </td>
                                        <td>
                                            {CurrencyFormat(paymentNote.amountPaid)}
                                        </td>
                                        <td>
                                            <div className={"label " + renderStatusColor(paymentNote.status)}>
                                                {paymentNote.status}
                                            </div>
                                        </td>
                                        <td>
                                            <Link to={"/payment-note/details/" + paymentNote.invoiceId} className={"text-base underline " + (paymentNote.status !== "paid" ? "text-ep-primary" : "text-gray-300")}>
                                                Make Payment
                                            </Link>
                                        </td>
                                        <td>
                                            <DropdownNew>
                                                <></>
                                                <div>
                                                    <Link to={"/payment-note/details/" + paymentNote.invoiceId} className="dropdown-item dropdown-item-icon">
                                                        <IconEdit />
                                                        View more
                                                    </Link>
                                                    <div onClick={() => downloadInvoicePDF(paymentNote)} className="dropdown-item dropdown-item-icon">
                                                        <IconDownload className="svg-fill-inherit" />
                                                        Download
                                                    </div>
                                                    <Link to={"/withdrawals?month=" + MonthName(paymentNote.month) + "&year=" + paymentNote.year} className="dropdown-item dropdown-item-icon">
                                                        <IconStack className="svg-fill-inherit" />
                                                        View Withdrawals
                                                    </Link>
                                                </div>
                                            </DropdownNew>
                                        </td>
                                    </tr>
                                )}

                            </Table.Body>
                        </Table>

                        <Maybe condition={paymentNotesLoading === false && paymentNotes?.invoices?.docs?.length > 0}>
                            <div className="mt-6">
                                <Pagination data={paymentNotes?.invoices} />
                            </div>
                        </Maybe>

                    </div>
                </div>
            </div>

        </div>
    )
}


export default List;